import { getCaseTypeLabel, parseApiDate } from '@/lib/formatters';

/**
 * Group inbox threads for the left nav: by client, then by case.
 *
 * A lawyer carries many clients, and one client can file more than one
 * application over time — so the client alone does not separate their threads.
 * Hence two levels: the person, then the matter, then the funders talking about
 * it.
 *
 * The case sub-heading is emitted only when a client actually has more than one
 * application. Drawing it for the common single-case client would be a header
 * above a single group, which is the same noise that makes grouping wrong for
 * funders entirely.
 *
 * Ordering is by most recent activity throughout — a client rises when any of
 * their conversations does, which is what "what needs me" means in an inbox.
 */

const time = (value) => {
  const d = parseApiDate(value);
  return d ? d.getTime() : 0;
};

const latest = (items, pick) => items.reduce((max, i) => Math.max(max, pick(i)), 0);

export const caseLabel = (thread) =>
  [
    thread.case_type ? getCaseTypeLabel(thread.case_type) : '',
    thread.accident_state,
  ]
    .filter(Boolean)
    .join(' · ');

export const groupThreadsByClient = (threads) => {
  const list = Array.isArray(threads) ? threads : [];

  const byClient = new Map();
  for (const t of list) {
    // Fall back to the application id rather than merging every unnamed client
    // into one group — an application still loading its name must not appear to
    // belong to a different person.
    const key = t.client_name || `app:${t.application_id}`;
    if (!byClient.has(key)) byClient.set(key, new Map());
    const byCase = byClient.get(key);
    if (!byCase.has(t.application_id)) byCase.set(t.application_id, []);
    byCase.get(t.application_id).push(t);
  }

  const groups = [];
  for (const [clientKey, byCase] of byClient) {
    const cases = [];
    for (const [applicationId, caseThreads] of byCase) {
      const sorted = [...caseThreads].sort((a, b) => time(b.last_message_at) - time(a.last_message_at));
      cases.push({
        applicationId,
        label: caseLabel(sorted[0]),
        threads: sorted,
        unreadCount: sorted.filter((t) => t.unread).length,
        lastActivity: latest(sorted, (t) => time(t.last_message_at)),
      });
    }
    cases.sort((a, b) => b.lastActivity - a.lastActivity);

    groups.push({
      clientName: byCase.values().next().value[0].client_name || 'Unnamed applicant',
      key: clientKey,
      cases,
      // Only meaningful when the client has more than one matter.
      showCaseHeadings: cases.length > 1,
      unreadCount: cases.reduce((n, c) => n + c.unreadCount, 0),
      lastActivity: latest(cases, (c) => c.lastActivity),
    });
  }

  groups.sort((a, b) => b.lastActivity - a.lastActivity);
  return groups;
};

export default groupThreadsByClient;
